import { supabase } from '@/utils/supabase';
import { hasVolumeBonusFoundation } from './volumeBonusFoundationService';

const PAYOUT_SELECT = `
  id,
  loan_number,
  borrower_name,
  loan_amount,
  payout_type,
  gross_amount,
  e_and_o_deduction,
  net_amount,
  hit_compensation_cap,
  splits_description,
  fund_date,
  status,
  company_total_revenue,
  company_commission_base,
  lo_commission_base,
  processing_fee,
  broker_split_percent,
  gross_check_received,
  lo_reimbursements_total,
  settlement_statement_path,
  final_check_path,
  combined_document_path,
  banker_recon_id,
  payment_amount,
  recaptured_amount,
  payment_check_number,
  payment_date,
  recipient:recipient_id (id, first_name, last_name),
  source_officer:source_officer_id (id, first_name, last_name),
  banker_recon:banker_recons!banker_recon_id (
    investor_purchase_date,
    actual_purchase_price,
    processing_fee,
    underwriting_fee,
    company_revenue,
    wire_from_warehouse,
    compensation_type,
    compensation_value,
    volume_bonus_amount,
    net_profit,
    interest_income,
    servicing_interest_income,
    flexclose_credit,
    fha_ufmip_flow_mode,
    fha_ufmip_premium_amount,
    fha_ufmip_financed_amount,
    fha_ufmip_remitted_date,
    fha_ufmip_remitted_by,
    fha_ufmip_remitted_amount,
    fha_ufmip_verification_code,
    fha_ufmip_capture_status,
    fha_ufmip_company_variance,
    overrides,
    override_bps,
    override_recipient:override_recipient_id (first_name, last_name)
  )
`;

// Cash volume bonuses are deployed separately from the portal. Older databases
// lack this optional column; all other query errors must remain visible.
const LEGACY_PAYOUT_SELECT = PAYOUT_SELECT.replace('    volume_bonus_amount,\n', '');
let volumeBonusColumnMissingUntil = 0;

async function readCompensationPayouts() {
  const query = (selection: string) => supabase
    .from('payouts')
    .select(selection)
    .order('fund_date', { ascending: false });

  if (Date.now() < volumeBonusColumnMissingUntil || await hasVolumeBonusFoundation() !== 'present') {
    return query(LEGACY_PAYOUT_SELECT);
  }
  const result = await query(PAYOUT_SELECT);
  if (result.error?.code === '42703' && /\bvolume_bonus_amount\b/.test(result.error.message)) {
    volumeBonusColumnMissingUntil = Date.now() + 10 * 60 * 1000;
    return query(LEGACY_PAYOUT_SELECT);
  }
  return result;
}

export interface CompensationReconLineItem {
  id: string;
  category: string;
  line_type: string;
  description: string;
  amount: number;
  reimbursable: boolean;
  nets_to_warehouse_advance: boolean | null;
  warehouse_retained_amount: number | null;
}

export interface CompensationReconDetails {
  loan_snapshot: Record<string, unknown> | null;
  line_items: CompensationReconLineItem[];
}

interface ReconDetailRecord {
  id: string;
  loan_type: unknown;
  line_items: Array<Omit<CompensationReconLineItem, 'amount' | 'warehouse_retained_amount'> & {
    amount: number | string;
    warehouse_retained_amount: number | string | null;
  }>;
}

const detailRequests = new Map<string, Promise<CompensationReconDetails>>();

/** Load only the opened recon; never pull the full frozen loan snapshot. */
export function getCompensationPayoutReconDetails(reconId: string): Promise<CompensationReconDetails> {
  const pending = detailRequests.get(reconId);
  if (pending) {
    return pending;
  }
  const request = (async () => {
    const { data, error } = await supabase.from('banker_recons').select(`
      id, loan_type:loan_snapshot->trans_details_loan_type,
      line_items:banker_recon_line_items (
        id, category, line_type, description, amount, reimbursable, nets_to_warehouse_advance, warehouse_retained_amount
      )
    `).eq('id', reconId).single();
    if (error) {
      throw error;
    }
    const record = data as unknown as ReconDetailRecord | null;
    if (!record || record.id !== reconId || !Array.isArray(record.line_items)) {
      throw new Error('Payout accounting details could not be verified.');
    }
    return {
      loan_snapshot: typeof record.loan_type === 'string' ? { trans_details_loan_type: record.loan_type } : null,
      line_items: record.line_items.map(item => ({
        ...item,
        line_type: item.line_type || item.id,
        amount: Number(item.amount || 0),
        reimbursable: Boolean(item.reimbursable),
        nets_to_warehouse_advance: item.nets_to_warehouse_advance ?? null,
        warehouse_retained_amount: item.warehouse_retained_amount === null || item.warehouse_retained_amount === undefined
          ? null : Number(item.warehouse_retained_amount)
      }))
    };
  })().finally(() => {
    if (detailRequests.get(reconId) === request) {
      detailRequests.delete(reconId);
    }
  });
  detailRequests.set(reconId, request);
  return request;
}

interface PayoutListRecord {
  payout_type: string;
  fund_date: string | null;
  banker_recon_id: string | null;
  banker_recon: { investor_purchase_date?: string | null; net_profit?: number | string | null } | null;
}

export async function getCompensationPayouts() {
  // A refreshed list must not join an accounting request from its prior load.
  detailRequests.clear();
  const result = await readCompensationPayouts();
  if (result.error || !result.data) {
    return result;
  }
  const records = result.data as unknown as PayoutListRecord[];
  const needsDetails = records.filter(record => record.payout_type === 'banked'
    && record.fund_date && record.banker_recon?.investor_purchase_date
    && (record.banker_recon.net_profit === null || record.banker_recon.net_profit === undefined
      || !Number.isFinite(Number(record.banker_recon.net_profit))));
  if (!needsDetails.length) {
    return result;
  }
  // Historical rows without frozen profit still need the canonical calculation
  // before totals/export become usable. Bound each request to one visible recon.
  const details = new Map<string, CompensationReconDetails>();
  for (const record of needsDetails) {
    if (!record.banker_recon_id) {
      throw new Error('Payout accounting details are missing a reconciliation.');
    }
    if (!details.has(record.banker_recon_id)) {
      details.set(record.banker_recon_id, await getCompensationPayoutReconDetails(record.banker_recon_id));
    }
  }
  return { ...result, data: records.map(record => {
    const detail = record.banker_recon_id && details.get(record.banker_recon_id);
    return detail ? { ...record, banker_recon: { ...record.banker_recon, ...detail } } : record;
  }) };
}
